import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useDecideReport, useReportsWithContext } from '../api/hooks';
import { DECISION_ACTIONS, REPORT_STATES, type DecisionAction, type ReportState, type ReportUser, type ReportWithContext } from '../api/types';
import { PageHeader } from '../app/Shell';
import { REPORT_REASON, REPORT_STATE, USER_STATUS } from '../app/labels';
import { Badge, Button, Card, ConfirmDialog, DataTable, Drawer, ErrorNote, Id, JsonView, KeyValues, QueryView, ReasonField, Section, Segmented, fmtAgo, fmtDateTime, reasonError, type Tone, toast } from '../ui';

const ACTION_INFO: Record<DecisionAction, { label: string; hint: string }> = {
  dismiss: { label: '駁回', hint: '沒有違規，以駁回結案。' },
  warn: { label: '警告', hint: '記錄一次警告，不改內容或帳戶。' },
  remove_message: { label: '移除訊息', hint: '所有人都不會再看到被檢舉的訊息。' },
  suspend_user: { label: '停用被檢舉者', hint: '停用被檢舉的帳戶，並在所有裝置登出。' },
};

const SEVERE = new Set(['minor_safety', 'sexual_content', 'hate', 'harassment']);
const reasonTone = (reason: string): Tone => (reason === 'minor_safety' ? 'danger' : SEVERE.has(reason) ? 'warn' : 'neutral');
const TARGET_LABEL: Record<string, string> = { message: '訊息', npc_output: '角色 AI 回覆', episode: '故事', panel: '漫畫格', chapter: '漫畫章節', player: '玩家（已移除）', room: '房間（已移除）' };

function isReportState(v: string | null): v is ReportState {
  return (REPORT_STATES as readonly string[]).includes(v ?? '');
}

function Who({ u }: { u: ReportUser | null }) {
  if (!u) return <span className="faint">—</span>;
  return (
    <Link to={`/users/${u._id}`} title={u._id}>
      {u.email ?? u.displayName ?? u.publicId ?? u._id.slice(0, 8)}
    </Link>
  );
}

/** Player reports with who filed them, who they're about, and the reported message itself. */
export function ModerationPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('state');
  const state: ReportState = isReportState(raw) ? raw : 'open';
  const reason = params.get('reason') ?? '';
  const reports = useReportsWithContext(state, reason || undefined);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = reports.data?.find((r) => r._id === selectedId) ?? null;
  const set = (next: { state?: ReportState; reason?: string }) => {
    const s = next.state ?? state;
    const r = next.reason ?? reason;
    setSelectedId(null);
    setParams({ ...(s === 'open' ? {} : { state: s }), ...(r ? { reason: r } : {}) });
  };

  return (
    <>
      <PageHeader
        title="檢舉審核"
        description="玩家檢舉；待處理的最舊在前。每個決定都會記錄在審計日誌。"
        actions={<Segmented label="檢舉狀態" value={state} options={REPORT_STATES.map((st) => ({ value: st, label: REPORT_STATE[st] }))} onChange={(v) => set({ state: v })} />}
      />
      <Card flush>
        <div className="filters">
          <label>
            原因
            <select value={reason} onChange={(e) => set({ reason: e.target.value })}>
              <option value="">全部</option>
              {Object.entries(REPORT_REASON).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          {reports.data && <span className="small faint">{reports.data.length >= 50 ? '顯示首 50 項' : `${reports.data.length} 項`}</span>}
        </div>
        <QueryView query={reports} loading="載入檢舉…">
          {(rows) => (
            <DataTable
              rows={rows}
              rowKey={(r) => r._id}
              onRowClick={(r) => setSelectedId(r._id)}
              selectedKey={selectedId}
              empty={state === 'open' ? '沒有待處理的檢舉。' : `沒有${REPORT_STATE[state]}的檢舉。`}
              columns={[
                { key: 'age', header: '檢舉時間', render: (r) => <span title={fmtDateTime(r.createdAt)} className="nowrap">{fmtAgo(r.createdAt)}</span>, sort: (r) => r.createdAt },
                { key: 'reason', header: '原因', render: (r) => <Badge tone={reasonTone(r.reason)}>{REPORT_REASON[r.reason] ?? r.reason}</Badge>, sort: (r) => r.reason },
                { key: 'target', header: '對象', render: (r) => <span className="nowrap">{TARGET_LABEL[r.targetType] ?? r.targetType}</span> },
                {
                  key: 'user',
                  header: '被檢舉者',
                  render: (r) => (
                    <span className="nowrap">
                      <Who u={r.target} />
                      {r.targetReportCount > 1 && (
                        <>
                          {' '}
                          <Badge tone="warn" title="這位玩家被檢舉的總次數">共 {r.targetReportCount} 次</Badge>
                        </>
                      )}
                    </span>
                  ),
                },
                { key: 'content', header: '內容 / 備註', render: (r) => <span className="truncate" style={{ display: 'block', maxWidth: 340 }}>{r.message?.body ?? r.note ?? <span className="faint">—</span>}</span> },
                { key: 'flags', header: '自動標記', render: (r) => (r.autoFlags.length ? r.autoFlags.map((f) => <Badge key={f} tone="danger">{f}</Badge>) : <span className="faint">—</span>) },
                ...(state === 'open' ? [] : [{ key: 'decision', header: '決定', render: (r: ReportWithContext) => (r.decision ? (ACTION_INFO[r.decision.action]?.label ?? r.decision.action) : '—') }]),
              ]}
            />
          )}
        </QueryView>
      </Card>
      <Drawer open={selected !== null} title="檢舉" onClose={() => setSelectedId(null)}>
        {selected && <ReportDetail key={selected._id} report={selected} onDecided={() => setSelectedId(null)} />}
      </Drawer>
    </>
  );
}

function ReportDetail({ report: r, onDecided }: { report: ReportWithContext; onDecided: () => void }) {
  return (
    <>
      <KeyValues
        items={[
          ['原因', <Badge tone={reasonTone(r.reason)}>{REPORT_REASON[r.reason] ?? r.reason}</Badge>],
          ['狀態', REPORT_STATE[r.state]],
          ['對象', <span>{TARGET_LABEL[r.targetType] ?? r.targetType} · <Id value={r.targetId} /></span>],
          [
            '被檢舉者',
            r.target ? (
              <span>
                <Who u={r.target} /> {r.target.status && <Badge tone={USER_STATUS[r.target.status].tone}>{USER_STATUS[r.target.status].label}</Badge>} · 共被檢舉 {r.targetReportCount} 次
              </span>
            ) : (
              '—'
            ),
          ],
          ['檢舉者', <Who u={r.reporter} />],
          ['檢舉時間', fmtDateTime(r.createdAt)],
        ]}
      />
      {r.message && (
        <Section title="被檢舉的訊息">
          <blockquote style={{ margin: 0, padding: 'var(--sp-2) var(--sp-3)', borderLeft: '3px solid var(--accent)', background: 'var(--surface-2)', whiteSpace: 'pre-wrap' }}>{r.message.body}</blockquote>
          <p className="small faint">
            {fmtDateTime(r.message.createdAt)} · 狀態 {r.message.moderation}
          </p>
        </Section>
      )}
      <Section title="檢舉者備註">{r.note ? <p style={{ whiteSpace: 'pre-wrap' }}>{r.note}</p> : <p className="faint">沒有備註。</p>}</Section>
      {r.autoFlags.length > 0 && (
        <Section title="自動標記">
          <div className="row">
            {r.autoFlags.map((f) => (
              <Badge key={f} tone="danger">
                {f}
              </Badge>
            ))}
          </div>
        </Section>
      )}
      <Section title="證據 (evidence)">
        <JsonView value={r.evidence} />
      </Section>
      {r.decision ? (
        <Section title="決定">
          <KeyValues
            items={[
              ['處理', ACTION_INFO[r.decision.action]?.label ?? r.decision.action],
              ['備註', r.decision.note],
              ['處理人', <Id value={r.decision.adminId} />],
              ['時間', fmtDateTime(r.decision.at)],
            ]}
          />
        </Section>
      ) : (
        <DecisionForm report={r} onDecided={onDecided} />
      )}
    </>
  );
}

function DecisionForm({ report, onDecided }: { report: ReportWithContext; onDecided: () => void }) {
  const [action, setAction] = useState<DecisionAction>('dismiss');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const decide = useDecideReport();

  const unavailable: Partial<Record<DecisionAction, string>> = {
    ...(report.targetType !== 'message' ? { remove_message: '只適用於被檢舉的訊息。' } : {}),
    ...(!report.targetUserId ? { suspend_user: '這個檢舉沒有被檢舉的玩家。' } : {}),
  };
  const valid = !reasonError(note) && !unavailable[action];

  const run = () =>
    decide.mutate(
      { reportId: report._id, action, note: note.trim() },
      {
        onSuccess: () => {
          setConfirming(false);
          toast(`已${ACTION_INFO[action].label}`);
          onDecided();
        },
      },
    );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setConfirming(true);
  };

  return (
    <Section title="處理">
      <form className="stack" onSubmit={submit}>
        <div className="stack" style={{ gap: 6 }} role="radiogroup" aria-label="處理方式">
          {DECISION_ACTIONS.map((a) => (
            <label key={a} className="row" style={{ alignItems: 'flex-start', flexWrap: 'nowrap', opacity: unavailable[a] ? 0.5 : 1, cursor: unavailable[a] ? 'not-allowed' : 'pointer' }}>
              <input type="radio" name="action" value={a} checked={action === a} disabled={!!unavailable[a]} onChange={() => setAction(a)} style={{ marginTop: 3 }} />
              <span>
                <strong>{ACTION_INFO[a].label}</strong>
                <span className="muted small" style={{ display: 'block' }}>
                  {unavailable[a] ?? ACTION_INFO[a].hint}
                </span>
              </span>
            </label>
          ))}
        </div>
        <ReasonField label="審核備註" value={note} onChange={setNote} hint="必填；與決定一起保存，並記錄在審計日誌。" />
        <div className="row">
          <Button type="submit" variant={action === 'suspend_user' ? 'danger' : 'primary'} disabled={!valid}>
            {ACTION_INFO[action].label}…
          </Button>
        </div>
      </form>
      <ConfirmDialog
        open={confirming}
        title={`確定「${ACTION_INFO[action].label}」？`}
        tone={action === 'suspend_user' || action === 'remove_message' ? 'danger' : 'primary'}
        confirmLabel={ACTION_INFO[action].label}
        busy={decide.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={run}
        error={decide.isError ? <ErrorNote error={decide.error} /> : null}
      >
        <p>{ACTION_INFO[action].hint}</p>
        <p className="muted small">備註：「{note.trim()}」</p>
      </ConfirmDialog>
    </Section>
  );
}
